/**
 * Rewrite a `<colgroup>`'s widths as percentages of their own total.
 *
 * this is the one operation that keeps a table inside its container. TipTap serialises
 * absolute pixel `<col>` widths, and under `table-layout: fixed` CSS makes a table as wide as the
 * GREATER of its own width and the sum of its columns
 *
 * `min-width` is cleared alongside, because `min-width` outranks `max-width` in CSS: a column left
 * with the `min-width` prosemirror-tables gives unsized columns would defeat the containment on its
 * own.
 *
 * @param {HTMLTableColElement} colgroup - the `<colgroup>` whose `<col>` children are rewritten.
 * @param {number[]} widths - one width per column, in column order. The list may be shorter than
 *   the colgroup, and entries may be missing; those columns are left untouched.
 * @param {number} [precision] - decimal places. Callers pass their own rather than sharing one:
 *   the render path emits 2dp into HTML that reaches the published page and the PDF, the editor
 *   uses 4dp so repeated drags accumulate less rounding error. Unifying them would change served
 *   markup.
 * @returns {boolean} `false` when the widths total zero or less, in which case nothing is written -
 *   callers use this to skip any follow-up work that only makes sense once columns are sized.
 */
export const applyColumnPercentages = (colgroup, widths, precision = 4) => {
  const total = widths.reduce((sum, width) => (Number.isFinite(width) ? sum + width : sum), 0);

  if (total <= 0) {
    return false;
  }

  Array.from(colgroup.children).forEach((col, index) => {
    if (Number.isFinite(widths[index])) {
      col.style.setProperty('width', `${((widths[index] / total) * 100).toFixed(precision)}%`);
      col.style.removeProperty('min-width');
    }
  });

  return true;
};

export default applyColumnPercentages;
